
import { useState, useEffect, useCallback } from 'react';
import { VehicleFormData } from '../types/vehicle';
import { authPpiApiAxiosWithRefresh } from '../services/api';

interface UseVehicleDataReturn {
  vehicleData: VehicleFormData | null;
  isManual: boolean;
  loading: boolean;
  error: string | null;
  updateVehicleData: (data: Partial<VehicleFormData>) => Promise<void>;
  refreshVehicleData: () => Promise<void>;
  hasUnsavedChanges: boolean;
}

export const useVehicleData = (docId: string | null): UseVehicleDataReturn => {
  const [vehicleData, setVehicleData] = useState<VehicleFormData | null>(null);
  const [isManual, setIsManual] = useState(false);
  const [originalData, setOriginalData] = useState<VehicleFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasUnsavedChanges = JSON.stringify(vehicleData) !== JSON.stringify(originalData);

  const fetchVehicleData = useCallback(async () => {
    if (!docId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await (await authPpiApiAxiosWithRefresh()).get(`/vehicle-data/${docId}`);
      if (response.data.success && response.data.data) {
        const backendData = response.data.data;
        const formattedData: VehicleFormData = {
          titleStatus: backendData.vehicleDetails?.titleStatus || '',
          VIN: backendData.vehicleDetails?.VIN || '',
          name: `${backendData.vehicleDetails?.Year || ''} ${backendData.vehicleDetails?.Make || ''} ${backendData.vehicleDetails?.Model || ''}`.trim(),
          Model: backendData.vehicleDetails?.Model || '',
          Make: backendData.vehicleDetails?.Make || '',
          Trim: backendData.vehicleDetails?.Trim || '',
          Engine: backendData.vehicleDetails?.Engine || '',
          Mileage: backendData.vehicleDetails?.Mileage || '',
          Transmission: backendData.vehicleDetails?.Transmission || '',
          Location: backendData.vehicleDetails?.Location || '',
          interiorColor: backendData.vehicleDetails?.interiorColor || '',
          exteriorColor: backendData.vehicleDetails?.exteriorColor || '',
          Drivetrain: backendData.vehicleDetails?.Drivetrain || '',
          Year: backendData.vehicleDetails?.Year || '',
          bodyClass: backendData.vehicleDetails?.BodyClass || backendData.vehicleDetails?.bodyClass || '',
          description: backendData.vehicleDescription || '',
          comments: '', 
          knownIssues: [],
          imageLinks: backendData.vehicleDetails?.imageLinks || [],
          Price: backendData.vehicleDetails?.Price || '',
          sellerType: backendData.vehicleDetails?.sellerType || '',
          listingType: backendData.vehicleDetails?.listingType || ''
        };
        const isManual = response.data.isManual;

        setVehicleData(formattedData);
        setOriginalData(formattedData);
        setIsManual(isManual);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch vehicle data');
    } finally {
      setLoading(false);
    }
  }, [docId]);

  const updateVehicleData = useCallback(async (updates: Partial<VehicleFormData>) => {
    if (!docId || !vehicleData) return;

    const updatedData = { ...vehicleData, ...updates };
    setVehicleData(updatedData);

    try {
      const backendFormat = {
        vehicleDetails: {
          titleStatus: updatedData.titleStatus,
          VIN: updatedData.VIN,
          Model: updatedData.Model,
          Make: updatedData.Make,
          Trim: updatedData.Trim,
          Engine: updatedData.Engine,
          Mileage: updatedData.Mileage,
          Transmission: updatedData.Transmission,
          Location: updatedData.Location,
          interiorColor: updatedData.interiorColor,
          exteriorColor: updatedData.exteriorColor,
          Drivetrain: updatedData.Drivetrain,
          Year: updatedData.Year,
          bodyClass: updatedData.bodyClass,
          imageLinks: updatedData.imageLinks,
          Price: updatedData.Price,
          sellerType: updatedData.sellerType
        },
        vehicleDescription: updatedData.description
      };

      const response = await (await authPpiApiAxiosWithRefresh()).put(`/vehicle-data/${docId}`, backendFormat);
      
      if (response.data.success) {
        setOriginalData(updatedData);
      } else {
        throw new Error(response.data.error || 'Failed to update vehicle data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update vehicle data');
      setVehicleData(vehicleData);
      throw err;
    }
  }, [docId, vehicleData]);

  const refreshVehicleData = useCallback(async () => {
    await fetchVehicleData();
  }, [fetchVehicleData]);

  useEffect(() => {
    if (docId) {
      fetchVehicleData();
    }
  }, [docId, fetchVehicleData]);

  return {
    vehicleData,
    isManual,
    loading,
    error,
    updateVehicleData,
    refreshVehicleData,
    hasUnsavedChanges
  };
};
